import axios, { AxiosResponse, AxiosError } from 'axios';
import { toast } from 'sonner';

interface ApiError {
  message: string;
  code?: string;
  status?: number;
}

// Create axios instance with default configuration
const api = axios.create({
  baseURL: (import.meta as any).env.VITE_API_URL ||
           (typeof window !== 'undefined' ? `${window.location.protocol}//${window.location.host}` : 'http://localhost:5000'),
  withCredentials: true, // Important for session-based auth
  timeout: 30000, // 30 second timeout
  headers: {
    'Content-Type': 'application/json',
  },
});

// Track retry attempts
const retryAttempts = new Map<string, number>();

// Request interceptor for logging and potential token handling
api.interceptors.request.use(
  (config: any) => {
    // Add timestamp for request tracking
    config.metadata = { startTime: Date.now() };
    
    // Log request in development
    if ((import.meta as any).env.DEV) {
    }
    
    return config;
  },
  (error) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for error handling and retry logic
api.interceptors.response.use(
  (response: AxiosResponse) => {
    // Log response time in development
    if ((import.meta as any).env.DEV && (response.config as any).metadata) {
      const duration = Date.now() - (response.config as any).metadata.startTime;
    }
    
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config;
    if (!originalRequest) return Promise.reject(error);

    const requestKey = `${originalRequest.method}-${originalRequest.url}`;
    const attempts = retryAttempts.get(requestKey) || 0;

    // Handle different error scenarios
    if (error.response?.status === 401) {
      // Authentication error - redirect to login
      handleAuthFailure();
      return Promise.reject(error);
    }

    if (error.response?.status === 403) {
      // Forbidden - show user-friendly message
      toast.error('You do not have permission to perform this action.');
      return Promise.reject(error);
    }

    if (error.response?.status === 429) {
      // Rate limiting - show user-friendly message and retry after delay
      toast.error('Too many requests. Please wait a moment and try again.');
      
      if (attempts < 3) {
        retryAttempts.set(requestKey, attempts + 1);
        await delay(2000 * (attempts + 1)); // Exponential backoff
        return api(originalRequest);
      }
      return Promise.reject(error);
    }

    if (error.response?.status != null && error.response.status >= 500) {
      // Server error - retry with exponential backoff
      if (attempts < 3) {
        retryAttempts.set(requestKey, attempts + 1);
        toast.error(`Server error. Retrying... (Attempt ${attempts + 1}/3)`);
        await delay(1000 * Math.pow(2, attempts)); // Exponential backoff
        return api(originalRequest);
      } else {
        toast.error('Server is experiencing issues. Please try again later.');
      }
    }

    if (error.code === 'NETWORK_ERROR' || error.code === 'ECONNABORTED') {
      // Network error or timeout
      if (attempts < 2) {
        retryAttempts.set(requestKey, attempts + 1);
        toast.error(`Connection issue. Retrying... (Attempt ${attempts + 1}/2)`);
        await delay(1000);
        return api(originalRequest);
      } else {
        toast.error('Unable to connect. Please check your internet connection.');
      }
    }

    // Clear retry attempts on successful response or final failure
    retryAttempts.delete(requestKey);

    return Promise.reject(error);
  }
);

function handleAuthFailure() {
  // Clear ALL localStorage data to ensure clean logout
  if (typeof window !== 'undefined') {
    
    // Clear all localStorage (this removes all cached data, auth tokens, preferences, etc.)
    localStorage.clear();
    
    // Alternative: Clear specific auth-related items if we want to preserve user preferences
    // localStorage.removeItem('auth_token');
    // localStorage.removeItem('refresh_token');
    // localStorage.removeItem('user');
    // localStorage.removeItem('pendingPlan');
  }
  
  // Show notification
  toast.error('Your session has expired. Please log in again.');
  
  // Redirect to login with current page as redirect target
  const currentPath = window.location.pathname;
  const redirectUrl = `/auth?redirect=${encodeURIComponent(currentPath)}`;
  
  // Force redirect to login page
  setTimeout(() => {
    window.location.href = redirectUrl;
  }, 100);
}

function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

// Enhanced API methods with better typing and error handling
export const apiService = {
  // GET request
  async get<T = any>(url: string, config = {}): Promise<T> {
    const response = await api.get<T>(url, config);
    return response.data;
  },

  // POST request
  async post<T = any>(url: string, data?: any, config = {}): Promise<T> {
    const response = await api.post<T>(url, data, config);
    return response.data;
  },

  // PUT request
  async put<T = any>(url: string, data?: any, config = {}): Promise<T> {
    const response = await api.put<T>(url, data, config);
    return response.data;
  },

  // PATCH request
  async patch<T = any>(url: string, data?: any, config = {}): Promise<T> {
    const response = await api.patch<T>(url, data, config);
    return response.data;
  },

  // DELETE request
  async delete<T = any>(url: string, config = {}): Promise<T> {
    const response = await api.delete<T>(url, config);
    return response.data;
  },

  // Upload file with progress tracking
  async uploadFile<T = any>(
    url: string, 
    file: File, 
    onProgress?: (progress: number) => void
  ): Promise<T> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post<T>(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent: any) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(progress);
        }
      },
    });

    return response.data;
  },
};

// Backward compatibility - keep the existing apiRequest function
export async function apiRequest(
  url: string,
  method: "GET" | "POST" | "PUT" | "DELETE" = "GET",
  body?: any
) {
  try {
    let response;
    
    switch (method) {
      case 'GET':
        response = await api.get(url);
        break;
      case 'POST':
        response = await api.post(url, body);
        break;
      case 'PUT':
        response = await api.put(url, body);
        break;
      case 'DELETE':
        response = await api.delete(url);
        break;
      default:
        throw new Error(`Unsupported method: ${method}`);
    }

    // Return response object that matches the original apiRequest behavior
    return {
      ok: true,
      status: response.status,
      json: async () => response.data,
      text: async () => JSON.stringify(response.data),
    };
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(
        (error.response?.data as any)?.message || 
        (error.response?.data as any)?.error || 
        error.message
      );
    }
    throw error;
  }
}

export default api;